import React, { useState } from 'react';
import { Button, ToastMessage, formatPhoneNumber } from '@cokitchen/cokitchen-components';
import LocalModal from '../base/local-modal';
import { ReactComponent as PaystackIcon } from 'assets/icons/paystack-icon.svg';
import { WalletIcon } from 'cokitchen-icons';
import { usePaystackPayment } from 'react-paystack';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { PaymentActionType } from 'contexts/dashboard.context/context';
import TopupWalletCard from 'components/topup-wallet-card';
import CurrencyInput from 'components/base/currency-input';
import { handleApiError } from 'utils/functions';
import { PaymentCardType } from 'models/payment.model';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const FundWalletModal = ({ isOpen, onClose }: Props) => {
  const BANI_PUBLIC_KEY = process.env.REACT_APP_BANI_PUBLIC_KEY;
  const {
    activeCompany,
    paymentCards,
    paystackProps,
    setPaystackProps,
    refetchActiveCompany,
    refetchCompanyWalletDetails
  } = useDashboardContext();
  const [paymentType, setPaymentType] = useState<PaymentActionType | null>(null);
  const [activeCard, setActiveCard] = useState<PaymentCardType | null>(null);
  const [view, setView] = useState<'options' | 'pay'>('options');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const initializePayment: any = usePaystackPayment({
    ...paystackProps,
    metadata: {
      ...paystackProps.metadata,
      customer: {
        ...paystackProps.metadata.customer,
        fund_business_wallet: true
      }
    }
  } as any);

  const companyId: string = activeCompany?.id;

  const handleOnSuccess = () => {
    toast(<ToastMessage type='success' message='Payment successful, processing wallet balance' />);
  };

  const payWithTransfer = () => {
    (window as any).BaniPopUp({
      amount,
      phoneNumber: formatPhoneNumber(activeCompany.user.phone_number),
      merchantKey: BANI_PUBLIC_KEY,
      email: activeCompany.user.email,
      firstName: activeCompany.user.first_name,
      lastName: activeCompany.user.last_name,
      bankTransfer: true,
      metadata: {
        customer: {
          webhook: true,
          fund_business_wallet: true,
          phone_number: formatPhoneNumber(activeCompany?.user?.phone_number),
          body: { company_id: activeCompany.id }
        }
      },
      callback: handleOnSuccess
    });
  };

  async function handlePayment() {
    if (!companyId) return;
    if (paymentType === 'transfer' && view === 'options') return setView('pay');
    if (paymentType === 'transfer') {
      payWithTransfer();
      await refetchActiveCompany();
      await refetchCompanyWalletDetails();
      return;
    }
    if (paymentType === 'paystack' && view === 'options') return setView('pay');
    if (paymentType === 'paystack' && activeCard && view === 'options') return setView('pay');
    setLoading(true);
    if (paymentType === 'paystack' && !activeCard)
      return initializePayment(
        (context: any) => {
          if (context.status === 'success') {
            toast(
              <ToastMessage
                type='success'
                message='Payment successful, processing wallet balance'
              />
            );
            refetchActiveCompany();
            refetchCompanyWalletDetails();
            onClose();
          } else toast(<ToastMessage type='error' message='Payment failed' />);
          setLoading(false);
        },
        () => {
          setLoading(false);
        }
      );

    try {
      await apiService.post('card/charge/', {
        data: {
          card_id: activeCard?.id,
          amount: amount,
          company_id: activeCompany?.id
        }
      });
      toast(
        <ToastMessage type='success' message='Payment successful, processing wallet balance' />
      );
      refetchActiveCompany();
      refetchCompanyWalletDetails();
      onClose();
    } catch (err) {
      handleApiError(err, 'Payment failed');
    } finally {
      setLoading(false);
    }
  }

  const isDisabled = () => (view === 'options' && !paymentType) || (view === 'pay' && !amount);

  return (
    <LocalModal
      title={view === 'options' ? 'Select a payment method' : 'How much do you want to top-up?'}
      footer={
        <div className='flex justify-end'>
          <Button
            loading={loading}
            variant='block'
            color='primary'
            disabled={isDisabled()}
            onClick={handlePayment}
            className='w-auto h-[41px] self-end text-sm'>
            {view === 'options' ? 'Continue' : 'Top-up'}
          </Button>
        </div>
      }
      isOpen={isOpen}
      onRequestClose={() => {
        onClose();
        setAmount('');
        setView('options');
        setPaymentType(null);
        setActiveCard(null);
      }}>
      {view === 'options' ? (
        <div className='w-full flex flex-col space-y-2'>
          {(paymentCards ?? []).map((card) => (
            <TopupWalletCard
              key={card.id}
              onClick={() => {
                setPaymentType('paystack');
                setActiveCard({ ...card });
              }}
              active={paymentType === 'paystack' && activeCard?.id === card.id}
              icon={<PaystackIcon />}
              description={`*** ${card.last_four_digit}`}
              title='Pay with Paystack'
            />
          ))}

          <TopupWalletCard
            onClick={() => {
              setPaymentType('paystack');
              setActiveCard(null);
            }}
            active={paymentType === 'paystack'}
            icon={<WalletIcon className='w-5 h-5 text-[#00C3F7]' />}
            description='Powered by paystack'
            title='Pay with card'
          />
          <TopupWalletCard
            onClick={() => {
              setPaymentType('transfer');
              setActiveCard(null);
            }}
            active={paymentType === 'transfer'}
            icon={<WalletIcon className='w-5 h-5 text-[#00C3F7]' />}
            description='Powered by bani'
            title='Pay with transfer'
          />
        </div>
      ) : (
        <div className='w-full flex flex-col gap-3'>
          <p
            onClick={() => setView('options')}
            className='text-[#0080E5] text-xs cursor-pointer pb-2 self-start'>
            Back to payment methods
          </p>
          <CurrencyInput
            label='Top-up Amount'
            placeholder='500,000'
            value={amount}
            onChange={(value) => {
              setAmount(value);
              setPaystackProps({
                ...paystackProps,
                amount: +(value || 0) * 100
              });
            }}
          />
        </div>
      )}
    </LocalModal>
  );
};

export default FundWalletModal;
